import { COMPONENT_ID_BASE64_ENCODED, PromotionComponentType } from 'src/models/promotion/PromotionTypes';

/** Canonical 8-4-4-4-12 hex form. Matched as a shape, since other id formats also contain hyphens. */
export const CANONICAL_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * What a decoded identifier must look like to be worth sending: printable, no whitespace, bounded length.
 *
 * Deliberately not a UUID check. `componentId` carries no `@pattern` in the Smithy model and the promotion
 * path applies none, so requiring a UUID would refuse ids that promote correctly and make bulk stricter
 * than the path it mirrors. What it does reject is a decode yielding control bytes or mojibake.
 */
const PLAUSIBLE_DECODED_ID_PATTERN = /^[\x21-\x7e]{1,128}$/;

export enum IdentifierResolution {
    ACCEPTED = 'ACCEPTED',
    DUPLICATE = 'DUPLICATE',
    RAW_UUID = 'RAW_UUID',
    DECODE_FAILED = 'DECODE_FAILED',
    DECODED_NOT_AN_ID = 'DECODED_NOT_AN_ID',
    /** Pasted a name that no component of the selected type carries. Set by the name resolver, not here. */
    NAME_NOT_FOUND = 'NAME_NOT_FOUND',
    /** Pasted a name several components share. Never resolved to one of them — see bulkNameResolver. */
    NAME_AMBIGUOUS = 'NAME_AMBIGUOUS',
}

/** One row of the paste, and what became of it. Rendered by IdentifierSummary. */
export interface ResolvedIdentifier {
    input: string;
    segment: string;
    resolution: IdentifierResolution;
    resolvedId?: string;
    decoded: boolean;
    message?: string;
}

export interface ParsedBulkIdentifiers {
    rows: ResolvedIdentifier[];
    acceptedIds: string[];
    overCapBy: number;
}

/**
 * Whether a pasted identifier for this type is the base64 form the page URL carries.
 *
 * TPA_WORKSHEET is absent from COMPONENT_ID_BASE64_ENCODED only because getPromotionComponentHref has no
 * case for it; the worksheet views decode their own route param, so a copied worksheet URL is encoded too.
 */
export function isPastedIdBase64Encoded(componentType: PromotionComponentType): boolean {
    return COMPONENT_ID_BASE64_ENCODED.has(componentType) || componentType === PromotionComponentType.TPA_WORKSHEET;
}

/**
 * Every route whose last path element is a component identifier, longest first so a route is not matched by
 * a shorter one sharing its start. The worksheet routes are included: they are not emitted as hrefs, but an
 * operator can copy one from the page.
 */
const COMPONENT_PATH_PREFIXES: string[] = [
    '/calculations/multidimensionalAllocation/calculation/',
    '/calculations/multidimensionalAllocation/sheet/',
    '/calculations/customDataTables/details/',
    '/calculations/tpAllocation/standalone/',
    '/calculations/tpAllocation/template/',
    '/calculations/tpAllocation/linked/',
    '/calculations/customCoa/details/',
];

/**
 * Resolves a full URL to the identifier it addresses; a bare value is returned untouched.
 *
 * An encoded id can contain '/', which is in the base64 alphabet, so splitting on it would truncate the id.
 * Only a scheme-bearing value is treated as a URL, and a known route yields everything after its prefix.
 * Any other URL falls back to its last segment, all that can be inferred with no route to anchor on.
 */
export function extractLastPathSegment(input: string): string {
    const trimmed = input.trim();
    if (!trimmed.includes('://')) {
        return trimmed;
    }
    const withoutQuery = trimmed.split(/[?#]/)[0];
    const matched = COMPONENT_PATH_PREFIXES.find((prefix) => withoutQuery.includes(prefix));
    if (matched) {
        return withoutQuery.slice(withoutQuery.indexOf(matched) + matched.length).replace(/\/+$/, '');
    }
    const segments = withoutQuery.split('/').filter((segment) => segment.length > 0);
    return segments.length > 0 ? segments[segments.length - 1] : '';
}

function decodeBase64(segment: string): string | null {
    try {
        return atob(segment);
    } catch (ex) {
        return null;
    }
}

function resolveSegment(segment: string, decodeExpected: boolean): ResolvedIdentifier {
    const base: ResolvedIdentifier = {
        input: segment,
        segment,
        resolution: IdentifierResolution.ACCEPTED,
        decoded: false,
    };

    if (!decodeExpected) {
        return { ...base, resolvedId: segment };
    }

    // A raw UUID is a recognisable mistake, so name it rather than leaving preflight to say "not found".
    if (CANONICAL_UUID_PATTERN.test(segment)) {
        return {
            ...base,
            resolution: IdentifierResolution.RAW_UUID,
            message: 'That looks like a UUID — paste the encoded ID from the page URL.',
        };
    }

    const decoded = decodeBase64(segment);
    if (decoded === null) {
        return {
            ...base,
            resolution: IdentifierResolution.DECODE_FAILED,
            message: 'Could not decode this ID. Copy the last segment of the component page URL.',
        };
    }

    // Refuses a decode that cannot be an identifier at all, and nothing narrower: whether the component
    // exists is preflight's question, so guessing at id shape here only rejects what it would accept.
    if (!PLAUSIBLE_DECODED_ID_PATTERN.test(decoded)) {
        return {
            ...base,
            resolution: IdentifierResolution.DECODED_NOT_AN_ID,
            decoded: true,
            resolvedId: decoded,
            message: 'Decoded, but the result is not an ID. Copy the last segment of the component page URL.',
        };
    }

    return { ...base, decoded: true, resolvedId: decoded };
}

/**
 * Splits on newlines and commas, trims, drops empties, then resolves each row by the selected type. One row
 * per identifier, so a bad decode is distinguishable from a bad identifier.
 *
 * A line carrying a URL is one identifier and is not comma-split, since a query string may contain a comma.
 *
 * Dedupe runs before the cap comparison, as the raw line count would refuse a paste the service accepts.
 * Pass a cap only once fetched — there is no default.
 */
export function parseBulkIdentifiers(
    raw: string,
    componentType: PromotionComponentType,
    cap?: number
): ParsedBulkIdentifiers {
    const decodeExpected = isPastedIdBase64Encoded(componentType);
    const lines = (raw || '')
        .split(/[\r\n]+/)
        .flatMap((line) => (line.includes('://') ? [line] : line.split(',')))
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

    const seen = new Set<string>();
    const rows: ResolvedIdentifier[] = [];
    const acceptedIds: string[] = [];

    for (const line of lines) {
        const segment = extractLastPathSegment(line);
        if (segment.length === 0) {
            rows.push({
                input: line,
                segment,
                resolution: IdentifierResolution.DECODE_FAILED,
                decoded: false,
                message: 'No identifier could be read from this row.',
            });
            continue;
        }

        const resolved = resolveSegment(segment, decodeExpected);
        resolved.input = line;

        if (resolved.resolution !== IdentifierResolution.ACCEPTED) {
            rows.push(resolved);
            continue;
        }

        const id = resolved.resolvedId as string;
        if (seen.has(id)) {
            rows.push({
                ...resolved,
                resolution: IdentifierResolution.DUPLICATE,
                message: 'Already listed above — promoted once.',
            });
            continue;
        }

        seen.add(id);
        rows.push(resolved);
        acceptedIds.push(id);
    }

    const overCapBy = typeof cap === 'number' && acceptedIds.length > cap ? acceptedIds.length - cap : 0;

    return { rows, acceptedIds, overCapBy };
}

/**
 * Newline-separated, the separator the service-side parser handles, so a copied list pastes straight back
 * for a corrective re-run.
 *
 * Ids are re-encoded for the types whose pasted form is encoded: the accepted ids are decoded, and a bare
 * decoded id is refused on the way back in, so copying them raw would produce an unusable list.
 */
export function formatIdsForClipboard(ids: string[], componentType?: PromotionComponentType): string {
    const encode = componentType != null && isPastedIdBase64Encoded(componentType);
    return ids.map((id) => (encode ? btoa(id) : id)).join('\n');
}
